import { usePublicWeddingWebsite } from '@bridebook/web/app-basic/lib/wedding-website/hooks/use-public-wedding-website';
import { useRouter } from 'next/router';
import { useState } from 'react';
import FooterBottom from '@bridebook/web/app-shared/components/footer/footer-bottom/footer-bottom';
import {
  GetWeddingWebsiteDataResponse,
  GetWeddingWebsiteDataResponseSuccess,
} from '@bridebook/web/pages/api/for/website/[websiteId]';
import { useThemedComponentStyles } from '@bridebook/web/lib/wedding-website/hooks/use-themed-component-styles';
import {
  FontPairs as LegacyFontPair,
  Palettes as LegacyPalette,
} from '@bridebook/web/lib/wedding-website/types';
import { IThemedComponentStyles } from '@bridebook/web/lib/wedding-website/utils/get-themed-component-styles';
import { PasswordProtectedEntry } from './view-mode/password-protected-entry';
import { Container } from '@bridebook/ui';
import FullpageLoader from '@bridebook/web/app-shared/components/fullpage-loader/fullpage-loader';
import {
  toLegacyFontPair,
  toLegacyPalette,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/utils';

interface LayoutWebsiteProps {
  children: (props: {
    websiteData: GetWeddingWebsiteDataResponseSuccess;
    themeStyles: IThemedComponentStyles;
    theme: {
      font: string;
      palette: string;
    };
  }) => React.ReactNode;
  withFooter?: boolean;
  initialWeddingWebsiteData?: GetWeddingWebsiteDataResponse;
}

const LayoutWebsite = ({
  children,
  withFooter = true,
  initialWeddingWebsiteData,
}: LayoutWebsiteProps) => {
  const router = useRouter();
  const { websiteId } = router.query as { websiteId: string };
  const [pin, setPin] = useState<string>('');

  const { data, isLoading, isFetching } = usePublicWeddingWebsite(
    websiteId,
    {
      ...(pin && {
        pin,
      }),
    },
    {
      keepPreviousData: true,
      ...(pin ? {} : { initialData: initialWeddingWebsiteData }),
    },
  );

  const { loading: isLoadingFonts, styles } = useThemedComponentStyles(
    data && data.resultCode !== 'error'
      ? toLegacyPalette(data.website.config.theme.palette)
      : LegacyPalette.Bridebook,
    data && data.resultCode !== 'error'
      ? toLegacyFontPair(data?.website.config.theme.font)
      : LegacyFontPair.Bridebook,
  );

  if (isLoading || isLoadingFonts) {
    return <FullpageLoader />;
  }

  if (!data) {
    return <div>Error loading website</div>;
  }

  // Show password entry if pin is missing or invalid
  if (data.resultCode === 'pin_missing' || data.resultCode === 'invalid_pin') {
    return (
      <>
        <PasswordProtectedEntry
          onPasswordEntered={(pin) => {
            setPin(pin);
          }}
          firstName={data.wedding.partners[0]}
          secondName={data.wedding.partners[1]}
          loading={isFetching}
          themeStyles={styles}
          palette={toLegacyPalette(data.website.config.theme.palette)}
          invalidPinError={data.resultCode === 'invalid_pin'}
        />
        {withFooter && (
          <Container>
            <FooterBottom localeSelect={null} />
          </Container>
        )}
      </>
    );
  }

  if (data.resultCode === 'ok') {
    return (
      <>
        {children({
          websiteData: data as GetWeddingWebsiteDataResponseSuccess,
          themeStyles: styles as IThemedComponentStyles,
          theme: data.website.config.theme,
        })}
        {withFooter && (
          <Container>
            <FooterBottom localeSelect={null} />
          </Container>
        )}
      </>
    );
  }

  // Fallback error state
  return <div>Error loading website</div>;
};

export default LayoutWebsite;
